import {
  AlertModal,
  ArrowIcon,
  Box,
  Flex,
  Heading,
  Text,
  useOpenModal,
} from '@modusign/design-system';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useToggle } from 'react-use';

import HelpButton from '@app/components/HelpButton';
import ModusignLogo from '@app/components/ModusignLogo';
import SegmentedControl from '@app/components/SegmentedControl';
import { useExternalDocumentPermission } from '@app/features/externalDocument/hooks/useExternalDocumentPermission';

import MultipleUploadPanel from './MultipleUploadPanel';
import SingleUploadPanel from './SingleUploadPanel';

export default function UploadPage() {
  const openModal = useOpenModal();
  const history = useHistory();

  const { createPermission, isLoading } = useExternalDocumentPermission();

  const [activeTab, setActiveTab] = useState<0 | 1>(0);

  const [shouldShowTooltip, toggleTooltip] = useToggle(false);

  useEffect(() => {
    const hasNoAuthority = !isLoading && createPermission.type === 'NO_AUTHORITY_PERMISSION';
    const hasNoRolePermission = !isLoading && createPermission.type === 'NO_ROLE_PERMISSION';
    const isLimitMeetedOrExceeded =
      !isLoading &&
      (createPermission.type === 'NO_LEFT_LIMIT' || createPermission.type === 'USAGE_EXCEEDED');

    if (hasNoAuthority || hasNoRolePermission) {
      openModal(
        (props) => (
          <AlertModal
            {...props}
            maxWidth="xxs"
            title="외부문서 업로드 불가"
            variant="warning"
            onClose={() => {
              props.onClose();
              history.goBack();
            }}
          >
            권한이 없어 계약을 등록할 수 없습니다.
            <Box
              bg="boxBackgroundNeutralBase"
              border="1px solid"
              borderColor="boxBorderNeutralBase"
              mt={6}
              px={6}
              py={7}
            >
              요금제로 제공되지 않는 기능이거나, 외부문서 관리 권한이 없습니다. 관리자에게 문의해
              주세요.
            </Box>
          </AlertModal>
        ),
        { keepOnUnmount: true }
      );
    } else if (isLimitMeetedOrExceeded) {
      openModal(
        (props) => (
          <AlertModal
            {...props}
            maxWidth="xxs"
            title="외부문서 업로드 불가"
            variant="warning"
            onClose={() => {
              props.onClose();
              history.goBack();
            }}
          >
            외부문서 잔여 사용량이 없어 계약을 등록할 수 없습니다.
            <Box
              bg="boxBackgroundNeutralBase"
              border="1px solid"
              borderColor="boxBorderNeutralBase"
              mt={6}
              px={6}
              py={7}
            >
              요금제로 제공되는 외부문서 잔여 사용량을 모두 소진하였거나, 사용량이 초과되었습니다.
              이용 중인 요금제를 확인해 주세요.
            </Box>
          </AlertModal>
        ),
        { keepOnUnmount: true }
      );
    }
  }, [history, openModal, isLoading, createPermission]);

  return (
    <Flex flexDirection="column" minHeight="100vh">
      <Flex
        alignItems="center"
        as="header"
        bg="darkGray700"
        height="65px"
        justifyContent="space-between"
      >
        <Flex
          alignItems="center"
          as="a"
          color="textNeutralLightest"
          flex="1 1 0"
          href={'/external-documents/list'}
        >
          <ArrowIcon direction="left" height="18px" mx={4} width="9px" />
          <ModusignLogo color="white" height={17} width={123} />
          <Text fontWeight="medium" ml={3}>
            외부문서
          </Text>
        </Flex>
        <Heading color="textNeutralLightest" flex="0 0 1" fontSize="sm" fontWeight="medium">
          계약 등록하기
        </Heading>
        <Flex alignItems="center" flex="1 1 0" justifyContent="flex-end" mr={4}>
          <HelpButton variant="darkIcon" />
        </Flex>
      </Flex>
      <Flex
        alignItems="center"
        as="main"
        flex="auto"
        flexDirection="column"
        mb="100px"
        role="tablist"
      >
        <Box as="nav" pt={9} width="450px">
          <SegmentedControl
            activeIndex={activeTab}
            tabs={['한 개씩 등록', '여러개 일괄 등록']}
            onChange={(tab) => {
              setActiveTab(tab);
              toggleTooltip(tab === 1);
              if (tab === 1) {
                setTimeout(() => toggleTooltip(false), 3000);
              }
            }}
          />
        </Box>
        <Box mt={6} p={4} role="tabpanel" width="min(768px, 100vw)">
          <Box display={activeTab === 0 ? 'block' : 'none'}>
            <SingleUploadPanel />
          </Box>
          <Box display={activeTab === 1 ? 'block' : 'none'}>
            <MultipleUploadPanel showMultipleUploadTooltip={shouldShowTooltip} />
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
}
